import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, Loader2, MoreHorizontal,
  Printer, Eye, Receipt, CreditCard, DollarSign, Building2
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

import { generateDocumentHTML } from '../../lib/documentTemplates';

const formatReceiptDate = (value) => {
  if (!value) return '';
  const text = String(value);
  // Date-only strings (YYYY-MM-DD) must not be parsed as UTC, which shifts
  // the day back in timezones west of UTC. Parse them as a local date.
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [y, m, d] = text.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString();
  }
  const date = new Date(text);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'online', label: 'Online Payment' },
];

const PaymentReceipts = () => {
  const { currentCompany } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    invoice_id: '',
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [receiptsRes, invoicesRes, clientsRes] = await Promise.all([
        api.get('/payment-receipts'),
        api.get('/invoices'),
        api.get('/clients')
      ]);
      setReceipts(receiptsRes.data);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getClient = (id) => clients.find(c => c.id === id);
  const getInvoice = (id) => invoices.find(i => i.id === id);

  const openForm = (receipt = null) => {
    if (receipt) {
      setEditingReceipt(receipt);
      setFormData({
        client_id: receipt.client_id || '',
        invoice_id: receipt.invoice_id || '',
        amount: receipt.amount || 0,
        payment_date: receipt.payment_date || new Date().toISOString().split('T')[0],
        payment_method: receipt.payment_method || 'bank_transfer',
        reference_number: receipt.reference_number || '',
        notes: receipt.notes || '',
      });
    } else {
      setEditingReceipt(null);
      setFormData({
        client_id: '', invoice_id: '', amount: 0, payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer', reference_number: '', notes: '',
      });
    }
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingReceipt) {
        await api.put(`/payment-receipts/${editingReceipt.id}`, formData);
        toast.success('Receipt updated');
      } else {
        await api.post('/payment-receipts', formData);
        toast.success('Receipt created');
      }
      setIsFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this receipt?')) {
      try {
        await api.delete(`/payment-receipts/${id}`);
        toast.success('Deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed');
      }
    }
  };

  const openPreview = (receipt) => {
    setSelectedReceipt(receipt);
    const client = getClient(receipt.client_id);
    const receiptDoc = {
      ...receipt,
      items: [{ description: `Payment for Invoice #${receipt.invoice_id?.slice(0, 8) || 'N/A'}`, quantity: 1, price: receipt.amount }],
      total: receipt.amount,
      subtotal: receipt.amount,
      tax: 0,
      client_name: client?.name || 'Client',
      client_email: client?.email || '',
    };
    const html = generateDocumentHTML('classic', 'payment_receipt', receiptDoc, currentCompany)
      .replace('INVOICE', 'PAYMENT RECEIPT')
      .replace('Due:', 'Payment Date:');
    setPreviewHtml(html);
    setIsPreviewOpen(true);
  };

  const handlePrint = (receipt) => {
    const client = getClient(receipt.client_id);
    const receiptDoc = {
      ...receipt, items: [{ description: 'Payment Received', quantity: 1, price: receipt.amount }],
      total: receipt.amount, client_name: client?.name || '',
    };
    const html = generateDocumentHTML('classic', 'payment_receipt', receiptDoc, currentCompany).replace('INVOICE', 'PAYMENT RECEIPT');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  // Auto-fill amount when invoice selected
  const handleInvoiceChange = (invoiceId) => {
    const invoice = getInvoice(invoiceId);
    setFormData({
      ...formData,
      invoice_id: invoiceId,
      client_id: invoice?.client_id || formData.client_id,
      amount: invoice?.total || formData.amount,
    });
  };

  const filteredReceipts = receipts.filter(r =>
    r.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getClient(r.client_id)?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate totals
  const totalReceived = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Payment Receipts</h1>
          <p className="text-slate-500 mt-1">Track and manage payments received from clients</p>
        </div>
        <Button onClick={() => openForm()} className="bg-teal-700 hover:bg-teal-800">
          <Plus className="h-4 w-4 mr-2" />Record Payment
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Received</p>
                <p className="text-2xl font-bold text-green-600">${totalReceived.toLocaleString()}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Receipts</p>
                <p className="text-2xl font-bold">{receipts.length}</p>
              </div>
              <Receipt className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">This Month</p>
                <p className="text-2xl font-bold">{receipts.filter(r => new Date(r.payment_date).getMonth() === new Date().getMonth()).length}</p>
              </div>
              <CreditCard className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search receipts..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Receipt #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Method</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReceipts.length > 0 ? (
                filteredReceipts.map((receipt) => (
                  <TableRow key={receipt.id}>
                    <TableCell className="font-medium">#{receipt.id.slice(0, 8).toUpperCase()}</TableCell>
                    <TableCell>{getClient(receipt.client_id)?.name || '-'}</TableCell>
                    <TableCell>{receipt.invoice_id ? `#${receipt.invoice_id.slice(0, 8)}` : '-'}</TableCell>
                    <TableCell className="text-right font-medium text-green-600">${(receipt.amount || 0).toLocaleString()}</TableCell>
                    <TableCell>{formatReceiptDate(receipt.payment_date)}</TableCell>
                    <TableCell><Badge variant="outline">{receipt.payment_method?.replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openPreview(receipt)}><Eye className="h-4 w-4 mr-2" />Preview</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePrint(receipt)}><Printer className="h-4 w-4 mr-2" />Print</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openForm(receipt)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(receipt.id)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={7} className="h-32 text-center text-slate-500"><Receipt className="h-8 w-8 mx-auto mb-2 text-slate-300" />No receipts found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editingReceipt ? 'Edit Receipt' : 'Record Payment'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Invoice (Optional)</Label>
                <Select value={formData.invoice_id} onValueChange={handleInvoiceChange}>
                  <SelectTrigger><SelectValue placeholder="Select invoice" /></SelectTrigger>
                  <SelectContent>
                    {invoices.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>#{inv.id.slice(0, 8)} - ${inv.total} ({getClient(inv.client_id)?.name})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Client *</Label>
                <Select value={formData.client_id} onValueChange={(val) => setFormData({ ...formData, client_id: val })} required>
                  <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Amount *</Label>
                  <Input type="number" min="0" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })} required />
                </div>
                <div className="grid gap-2">
                  <Label>Payment Date *</Label>
                  <Input type="date" value={formData.payment_date} onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Payment Method</Label>
                  <Select value={formData.payment_method} onValueChange={(val) => setFormData({ ...formData, payment_method: val })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Reference #</Label>
                  <Input value={formData.reference_number} onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })} placeholder="Cheque/Transaction #" />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editingReceipt ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Payment Receipt Preview</DialogTitle></DialogHeader>
          
          <div className="border rounded-lg overflow-hidden mt-4" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Close</Button>
            <Button variant="outline" onClick={() => selectedReceipt && handlePrint(selectedReceipt)}><Printer className="h-4 w-4 mr-2" />Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentReceipts;
